<template>
    <component :is="tag" v-bind="linkProps" @click="handleClick" class="chip" :style="style"
               :class="[colorString ? 'chip-' + colorString : '', sizeString ? 'chip-' + sizeString : '', {'is-active': hasActiveState, 'has-icon': !!icon, 'is-clickable': clickable && !disabled, 'opacity-75': disabled, 'text-white': !!colorHex}]">
        <Icon v-if="icon" :icon="icon" :size="iconSize" prominent/>
        <slot name="left"/>
        <span class="chip__content truncate">
            <slot/>
        </span>
		<div v-if="close" @click.stop="$emit('click:close')">
			<Icon :icon="icons.close" class="ml-8 cursor-pointer" :x-small="small || xSmall" :small="!small && !xSmall"/>
		</div>
        <tooltip v-if="help" bottom :text="help" with-touch>
            <Icon :icon="icons.help" class="ml-8" :xx-small="xSmall" :x-small="small" :small="!small && !xSmall"/>
        </tooltip>
        <slot name="right"/>
    </component>
</template>

<script>
import Colorable from "../../mixins/Colorable";
    import Sizeable from "../../mixins/Sizeable";
    import Linkable from "../../mixins/Linkable";
    import {formatDimension} from "@spoferan/spoferan-ts-core";
	import {closeIcon, helpIcon} from "../../config";
    export default {
        name: "Chip",

		emits: ['click:close'],

        mixins: [Colorable, Sizeable, Linkable],

        props: {
            // An icon to display as prefix on the chip
            icon: {
                type: String,
                default: null
            },

            // Whether a close icon shall be displayed
            close: {
                type: Boolean,
                default: false
            },

            // A help text to show within a help tooltip
            help: {
                type: String,
                default: null
            },

            maxWidth: {
                type: [String, Number],
                default: null
            },
        },

		data() {
			return {
				icons: {
					close: closeIcon,
					help: helpIcon,
				}
			}
		},

        computed: {
            hasActiveState() {
                return !this.disabled && (this.isActive || this.hasActiveTo)
            },

            tag() {
                if (this.absoluteTo) {
                    return 'a';
                }
                if (this.relativeTo) {
                    return resolveComponent('NuxtLink');
                }

                return 'span';
            },

            style() {
                const style = {};

                if (this.colorHex) {
                    style.backgroundColor = this.colorHex;
                }

                if (this.maxWidth) {
                    style.maxWidth = formatDimension(this.maxWidth)
                }

                return style;
            },

            isClickable() {
                return this.isClickable || this.tag !== 'span';
            },

            iconSize() {
                switch (this.sizeString) {
                    case '2xs':
                        return 8;
                    case 'xs':
                        return 9;
                    case 'sm':
                        return 12;
                    case 'lg':
                        return 18;
                    case 'xl':
                        return 20;
                    case '2xl':
                        return 22;
                }

                return 16;
            }
        }
    }
</script>

<style>
/*********************
  Inputs
**********************/
/*********************
  Typography
**********************/
/*********************
  Inputs
**********************/
/*********************
  Shadows
**********************/
/*********************
  Layout
**********************/
/*********************
  Card
**********************/
/*********************
  Opacity
**********************/
.app .chip {
  align-items: center;
  display: inline-flex;
  line-height: 20px;
  max-width: 100%;
  outline: none;
  padding: 0 12px;
  position: relative;
  text-decoration: none;
  transition: box-shadow 0.28s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.28s, height 0.4s;
  vertical-align: middle;
  white-space: nowrap;
  border-radius: 16px;
  font-size: 13.5px;
  height: 34px;
  color: #5e6b6b;
  background-color: #ececee;
}
.app .chip.is-clickable {
  cursor: pointer;
}
.app .chip.is-clickable:hover {
  color: #5e6b6b;
}
.app .chip.is-clickable:hover:before {
  opacity: 0.04;
}
.app .chip.is-clickable:focus:before {
  opacity: 0.16;
}
.app .chip.has-icon {
  padding-left: 34px;
}
.app .chip.has-icon > .icon:first-child {
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  border-radius: 100%;
}
.app .chip:before {
  background-color: currentColor;
  bottom: 0;
  border-radius: inherit;
  content: "";
  left: 0;
  opacity: 0;
  position: absolute;
  pointer-events: none;
  right: 0;
  top: 0;
}
.app .chip .chip__content {
  max-width: 100%;
  width: 100%;
  text-align: center;
  height: 34px;
  line-height: 34px;
}
.app .chip .chip__content .icon {
  line-height: inherit;
  height: inherit;
}
.app .chip.is-active:before, .app .chip:active:before {
  opacity: 0.12;
}
.app .chip.chip-white {
  color: #ffffff;
  background-color: #ffffff;
}
.app .chip.chip-white:focus, .app .chip.chip-white.is-active {
  background-color: white;
}
.app .chip.chip-white.is-clickable:hover {
  background-color: white;
  color: #ffffff;
}
.app .chip.chip-white.chip-text {
  background-color: transparent;
  color: #ffffff;
}
.app .chip.chip-black {
  color: #ffffff;
  background-color: #2e3535;
}
.app .chip.chip-black:focus, .app .chip.chip-black.is-active {
  background-color: #333a3a;
}
.app .chip.chip-black.is-clickable:hover {
  background-color: #333a3a;
  color: #ffffff;
}
.app .chip.chip-black.chip-text {
  background-color: transparent;
  color: #2e3535;
}
.app .chip.chip-primary {
  color: #ffffff;
  background-color: #008200;
}
.app .chip.chip-primary:focus, .app .chip.chip-primary.is-active {
  background-color: #008c00;
}
.app .chip.chip-primary.is-clickable:hover {
  background-color: #008c00;
  color: #ffffff;
}
.app .chip.chip-primary.chip-text {
  background-color: transparent;
  color: #008200;
}
.app .chip.chip-secondary {
  color: #ffffff;
  background-color: #f0820a;
}
.app .chip.chip-secondary:focus, .app .chip.chip-secondary.is-active {
  background-color: #f5870f;
}
.app .chip.chip-secondary.is-clickable:hover {
  background-color: #f5870f;
  color: #ffffff;
}
.app .chip.chip-secondary.chip-text {
  background-color: transparent;
  color: #f0820a;
}
.app .chip.chip-error {
  color: #ffffff;
  background-color: #FA5555;
}
.app .chip.chip-error:focus, .app .chip.chip-error.is-active {
  background-color: #fa5f5f;
}
.app .chip.chip-error.is-clickable:hover {
  background-color: #fa5f5f;
  color: #ffffff;
}
.app .chip.chip-error.chip-text {
  background-color: transparent;
  color: #FA5555;
}
.app .chip.chip-success {
  color: #ffffff;
  background-color: #47a447;
}
.app .chip.chip-success:focus, .app .chip.chip-success.is-active {
  background-color: #4aab4a;
}
.app .chip.chip-success.is-clickable:hover {
  background-color: #4aab4a;
  color: #ffffff;
}
.app .chip.chip-success.chip-text {
  background-color: transparent;
  color: #47a447;
}
.app .chip.chip-warning {
  color: #ffffff;
  background-color: #EB9E05;
}
.app .chip.chip-warning:focus, .app .chip.chip-warning.is-active {
  background-color: #f5a505;
}
.app .chip.chip-warning.is-clickable:hover {
  background-color: #f5a505;
  color: #ffffff;
}
.app .chip.chip-warning.chip-text {
  background-color: transparent;
  color: #EB9E05;
}
.app .chip.chip-transparent {
  color: #ffffff;
  background-color: transparent;
}
.app .chip.chip-transparent:focus, .app .chip.chip-transparent.is-active {
  background-color: rgba(5, 5, 5, 0);
}
.app .chip.chip-transparent.is-clickable:hover {
  background-color: rgba(5, 5, 5, 0);
  color: #ffffff;
}
.app .chip.chip-transparent.chip-text {
  background-color: transparent;
  color: transparent;
}
.app .chip.chip-2xs {
  border-radius: 6px;
  font-size: 8px;
  height: 12px;
  padding: 0 4px;
}
.app .chip.chip-2xs .chip__content {
  height: 12px;
  line-height: 12px;
}
.app .chip.chip-xs {
  border-radius: 8px;
  font-size: 10px;
  height: 16px;
  padding: 0 8px;
}
.app .chip.chip-xs .chip__content {
  height: 16px;
  line-height: 16px;
}
.app .chip.chip-xs.has-icon {
  padding-left: 20px;
}
.app .chip.chip-xs.has-icon > .icon:first-child {
  left: 4px;
}
.app .chip.chip-sm {
  border-radius: 12px;
  font-size: 12px;
  height: 24px;
  padding: 0 10px;
}
.app .chip.chip-sm .chip__content {
  height: 24px;
  line-height: 24px;
}
.app .chip.chip-sm.has-icon {
  padding-left: 26px;
}
.app .chip.chip-sm.has-icon > .icon:first-child {
  left: 6px;
}
.app .chip.chip-lg {
  border-radius: 22px;
  font-size: 15px;
  height: 44px;
}
.app .chip.chip-lg .chip__content {
  height: 44px;
  line-height: 44px;
}
.app .chip.chip-lg.has-icon {
  padding-left: 44px;
}
.app .chip.chip-lg.has-icon > .icon:first-child {
  left: 12px;
}
.app .chip.chip-xl {
  border-radius: 27px;
  font-size: 16px;
  height: 54px;
}
.app .chip.chip-xl .chip__content {
  height: 54px;
  line-height: 54px;
}
</style>